import { message as messageAntd } from 'antd'
import { useCallback, useRef, useState } from 'react'
import { Api } from '~/core/trpc'
import { Toolbox } from '~/plugins/toolbox/shared'
import { Extensions } from '../../common'

type Options = {
  providerAccountId: string
  organizationMemberId: string
  extensions?: Extensions[]
  isMultiple?: boolean
}

export function useOneDrivePicker(options: Options) {
  const [pickedItems, setPickedItems] = useState(null)
  const winRef = useRef<Window | null>(null)
  const portRef = useRef<MessagePort | null>(null)

  const channelId = useRef(Toolbox.Utility.getUUID())

  const findManyFilesAction = Api.dataConnector.actionRun.useMutation()

  const {
    providerAccountId,
    organizationMemberId,
    extensions = [],
    isMultiple,
  } = options

  const params = {
    sdk: '8.0',
    messaging: {
      origin: window.origin,
      channelId: channelId.current,
    },
    typesAndSources: {
      mode: 'files',
    },
  }

  if (isMultiple) {
    params['selection'] = {
      mode: 'multiple',
    }
  }

  if (extensions?.length > 0) {
    params['typesAndSources']['filters'] = extensions
  }

  const openPopup = useCallback(() => {
    const baseUrlOneDrive = 'https://onedrive.live.com'

    const queryString = new URLSearchParams({
      filePicker: JSON.stringify(params),
    })

    const url = baseUrlOneDrive + '/picker' + `?${queryString}`

    winRef.current = window.open(url, 'Picker', 'width=800,height=600')

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== winRef.current) return

      const message = event.data

      if (
        message.type === 'initialize' &&
        message.channelId === params.messaging.channelId
      ) {
        portRef.current = event.ports[0]
        portRef.current.addEventListener('message', e => messageListener(e))
        portRef.current.start()
        portRef.current.postMessage({ type: 'activate' })
      }
    }

    const messageListener = async (message: MessageEvent) => {
      const port = portRef.current

      if (!port) {
        return
      }

      switch (message.data.type) {
        case 'notification':
          // ignore - useful for debugging
          break

        case 'command':
          port.postMessage({
            type: 'acknowledge',
            id: message.data.id,
          })

          // ! To sort without disabling eslint
          // eslint-disable-next-line no-case-declarations
          const command = message.data.data

          switch (command.command) {
            case 'close':
              winRef.current?.close()
              break

            case 'pick':
              // ! To sort without disabling eslint
              // eslint-disable-next-line no-case-declarations
              const itemIds = command.items.map(item => item.id)

              // ! To sort without disabling eslint
              // eslint-disable-next-line no-case-declarations
              const { response } = await findManyFilesAction.mutateAsync({
                organizationMemberId,
                providerAccountId,
                providerKey: 'oneDrive',
                providerActionKey: 'findManyFiles',
                args: {
                  fileIds: itemIds,
                },
              })

              if (response.files.length === 0) {
                messageAntd.error(
                  `Couldn't load the files picked. Make sure you login with your connected OneDrive account.`,
                )
              } else {
                setPickedItems(response.files)
              }

              port.postMessage({
                type: 'result',
                id: message.data.id,
                data: {
                  result: 'success',
                },
              })
              winRef.current?.close()
              break

            default:
              port.postMessage({
                result: 'error',
                error: {
                  code: 'unsupportedCommand',
                  message: command.command,
                },
                isExpected: true,
              })
              break
          }

          break
      }
    }

    window.addEventListener('message', handleMessage, { once: true })
  }, [providerAccountId, organizationMemberId])

  return { openPopup, pickedItems }
}
